import { useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import { format, startOfDay, startOfMonth } from 'date-fns'
import type { Locale } from 'date-fns'
import { useTranslation } from 'libs/i18n'
import { Label } from 'shared/atoms'
import { WithClassName } from 'shared/types/general.types'
import { cn } from 'utils/style'
import {
	CustomDatePickerButton,
	DatePickerClearButton,
	DatePickerVariant
} from '../Datepicker/Button/DatepickerButton'
import { calendarProps, CalendarProvider } from '../Datepicker/Calendar/Calendar'
import { getDateLocale } from '../Datepicker/utils/date'

type DateRange = [Date | null, Date | null]

type DatePickerRangeProps = WithClassName<{
	start: Date | undefined
	end: Date | undefined
	label?: string
	variant?: DatePickerVariant
	onChange: (date: DateRange) => void
}>

const getSummary = (start: Date, end: Date | null, locale: Locale) =>
	end
		? `${format(start, 'd MMM', { locale })} – ${format(end, 'd MMMM yyyy', { locale })}`
		: `${format(start, 'd MMMM yyyy', { locale })} – …`

export const DatePickerRange = ({
	start,
	end,
	label,
	className,
	variant = 'form',
	onChange
}: DatePickerRangeProps) => {
	const { t, i18n } = useTranslation()
	const locale = getDateLocale(i18n.language)
	const pickerRef = useRef<DatePicker>(null)
	const [isOpen, setIsOpen] = useState(false)
	const [draft, setDraft] = useState<DateRange | null>(null)
	const [rangeStart, rangeEnd] = draft ?? [start ?? null, end ?? null]

	const apply = (range: DateRange) => {
		setDraft(null)
		onChange(range)
	}

	const close = () => {
		pickerRef.current?.setOpen(false, true)
		pickerRef.current?.setFocus()
	}

	const select = (range: DateRange) => {
		if (!range[1]) {
			setDraft(range)
			return
		}

		apply(range)
		close()
	}

	const selectThisMonth = () => {
		const today = startOfDay(new Date())

		apply([startOfMonth(today), today])
		close()
	}

	return (
		<div className={cn('flex w-full flex-col gap-1.5', className)}>
			<Label text={label ?? t('Date')} />
			<div className="relative">
				<CalendarProvider
					value={{
						summary: rangeStart ? getSummary(rangeStart, rangeEnd, locale) : t('No range chosen'),
						actionLabel: t('This month'),
						isRange: true,
						onAction: selectThisMonth,
						onClear: () => setDraft([null, null])
					}}>
					<DatePicker
						{...calendarProps}
						ref={pickerRef}
						locale={locale}
						startDate={rangeStart}
						endDate={rangeEnd}
						placeholderText={t('Select date range')}
						selectsRange
						customInput={
							<CustomDatePickerButton
								startDate={rangeStart ?? undefined}
								endDate={rangeEnd ?? undefined}
								variant={variant}
								isOpen={isOpen}
								isRange
								hasClearButton={!!rangeStart}
							/>
						}
						onChange={select}
						onInputClick={() => {
							if (isOpen) close()
						}}
						onCalendarOpen={() => setIsOpen(true)}
						onCalendarClose={() => {
							setIsOpen(false)
							if (draft) apply(draft)
						}}
					/>
				</CalendarProvider>
				{!!rangeStart && (
					<DatePickerClearButton
						label={t('Clear date range')}
						variant={variant}
						onClick={() => apply([null, null])}
					/>
				)}
			</div>
		</div>
	)
}
